// src/components/admin/ecc/PricingImpactProjection.jsx
// ECC Track B-Extension: Pricing Impact Projection Component
//
// SYSTEM CONTRACT:
// - Admin-only visibility
// - Projection-only (no runtime impact)
// - Derived from simulation outputs
//
// PURPOSE:
// - Show subscription tier headroom/overage risk
// - Show credit bundle margin projections
// - Show effective $/hour, $/question pricing

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  DollarSign, Users, ShoppingBag, Clock, MessageSquare, 
  AlertTriangle, CheckCircle, AlertCircle, HelpCircle,
  TrendingUp, TrendingDown, Minus
} from 'lucide-react';

// ============================================================================
// RISK BADGE COMPONENT
// ============================================================================
const RiskBadge = ({ risk }) => {
  const styles = {
    low: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Low Risk' },
    medium: { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertCircle, label: 'Medium Risk' },
    high: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle, label: 'High Risk' }
  };
  
  const style = styles[risk] || styles.medium;
  const Icon = style.icon;
  
  return (
    <Badge className={`${style.bg} ${style.text} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {style.label}
    </Badge>
  );
};

// ============================================================================
// HEADROOM INDICATOR
// ============================================================================
const HeadroomIndicator = ({ headroom, monthlyCredits }) => {
  const isOverage = headroom < 0;
  const pct = monthlyCredits > 0 ? Math.abs(headroom / monthlyCredits * 100) : 0;
  
  return (
    <div className={`flex items-center gap-1 font-medium ${isOverage ? 'text-red-600' : 'text-green-600'}`}>
      {isOverage ? (
        <>
          <TrendingDown className="w-4 h-4" />
          <span>-{Math.abs(headroom).toFixed(1)} ({pct.toFixed(0)}% over)</span>
        </>
      ) : headroom > 0 ? (
        <>
          <TrendingUp className="w-4 h-4" />
          <span>+{headroom.toFixed(1)} ({pct.toFixed(0)}% buffer)</span>
        </>
      ) : (
        <>
          <Minus className="w-4 h-4" />
          <span>Exact match</span>
        </>
      )}
    </div>
  );
};

// ============================================================================
// MARGIN INDICATOR
// ============================================================================
const MarginIndicator = ({ marginPct }) => {
  const getColor = () => {
    if (marginPct >= 80) return 'text-green-600';
    if (marginPct >= 50) return 'text-emerald-600';
    if (marginPct >= 30) return 'text-amber-600';
    return 'text-red-600';
  };
  
  return (
    <span className={`font-semibold ${getColor()}`}>
      {marginPct.toFixed(1)}%
    </span>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const PricingImpactProjection = ({ projection, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          <div className="animate-pulse">Computing pricing projection...</div>
        </CardContent>
      </Card>
    );
  }
  
  if (!projection) {
    return null;
  }
  
  const { meta, derived, subscription_tiers, credit_bundles } = projection;
  
  const formatCurrency = (val, decimals = 2) => {
    if (val === null || val === undefined) return '—';
    return `$${Number(val).toFixed(decimals)}`;
  };
  
  return (
    <Card className="border-purple-200 bg-purple-50/30">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-purple-600" />
            Pricing Impact Projection
          </CardTitle>
          <Badge className="bg-purple-100 text-purple-700 text-xs">
            Projection Only
          </Badge>
        </div>
        <CardDescription className="text-xs">
          How current pricing constructs would perform under this simulation — no runtime impact
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-5">
        {/* Non-Authority Banner */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-purple-200 bg-purple-50 text-xs text-purple-700">
          <HelpCircle className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>Projection Only</strong> — Derived from simulation. Uses current tiers/bundles as defined in Pricing admin. No runtime impact.
          </span>
        </div>
        
        {/* Effective Unit Pricing */}
        {derived?.effective && (
          <div className="p-4 bg-white rounded-lg border border-purple-100">
            <h4 className="font-medium text-purple-700 mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Effective Unit Pricing
            </h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-purple-50 rounded-lg">
                <Clock className="w-5 h-5 mx-auto mb-1 text-purple-500" />
                <div className="text-lg font-bold text-purple-700">
                  {formatCurrency(derived.effective.usd_per_listen_hour, 4)}
                </div>
                <div className="text-xs text-purple-500">per listening hour</div>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <MessageSquare className="w-5 h-5 mx-auto mb-1 text-purple-500" />
                <div className="text-lg font-bold text-purple-700">
                  {formatCurrency(derived.effective.usd_per_ai_question, 4)}
                </div>
                <div className="text-xs text-purple-500">per AI question</div>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <div className="text-xs mb-1 text-purple-400">1K chars</div>
                <div className="text-lg font-bold text-purple-700">
                  {formatCurrency(derived.effective.usd_per_1000_chars, 4)}
                </div>
                <div className="text-xs text-purple-500">per 1,000 AI chars</div>
              </div>
            </div>
          </div>
        )}
        
        {/* Subscription Tier Projections */}
        {subscription_tiers && subscription_tiers.length > 0 && (
          <div className="p-4 bg-white rounded-lg border border-purple-100">
            <h4 className="font-medium text-purple-700 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Subscription Tier Performance
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-purple-100">
                    <th className="text-left py-2 font-medium text-purple-600">Tier</th>
                    <th className="text-right py-2 font-medium text-purple-600">Price</th>
                    <th className="text-right py-2 font-medium text-purple-600">Credits</th>
                    <th className="text-right py-2 font-medium text-purple-600">Projected Use</th>
                    <th className="text-right py-2 font-medium text-purple-600">Headroom</th>
                    <th className="text-center py-2 font-medium text-purple-600">Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {subscription_tiers.map((tier) => (
                    <tr key={tier.tier_id || tier.tier_code} className="border-b border-purple-50">
                      <td className="py-2 font-medium">{tier.name}</td>
                      <td className="text-right py-2">{formatCurrency(tier.monthly_price_usd)}/mo</td>
                      <td className="text-right py-2">{tier.monthly_credits}</td>
                      <td className="text-right py-2">{tier.projected_monthly_consumption_credits.toFixed(1)}</td>
                      <td className="text-right py-2">
                        <HeadroomIndicator 
                          headroom={tier.headroom_credits} 
                          monthlyCredits={tier.monthly_credits} 
                        />
                      </td>
                      <td className="text-center py-2">
                        <RiskBadge risk={tier.risk} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-purple-400 mt-2 italic">
              Risk = High if average user exceeds included credits, Medium if within 20%, Low otherwise
            </p>
          </div>
        )}
        
        {/* Credit Bundle Projections */}
        {credit_bundles && credit_bundles.length > 0 && (
          <div className="p-4 bg-white rounded-lg border border-purple-100">
            <h4 className="font-medium text-purple-700 mb-3 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" />
              Credit Bundle Margins
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-purple-100">
                    <th className="text-left py-2 font-medium text-purple-600">Bundle</th>
                    <th className="text-right py-2 font-medium text-purple-600">Credits</th>
                    <th className="text-right py-2 font-medium text-purple-600">Price</th>
                    <th className="text-right py-2 font-medium text-purple-600">Proj. Cost</th>
                    <th className="text-right py-2 font-medium text-purple-600">Margin $</th>
                    <th className="text-right py-2 font-medium text-purple-600">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {credit_bundles.map((bundle) => (
                    <tr key={bundle.bundle_id || bundle.bundle_code} className="border-b border-purple-50">
                      <td className="py-2">
                        <span className="font-medium">{bundle.name}</span>
                        {bundle.badge && (
                          <Badge className="ml-2 text-xs bg-amber-100 text-amber-700">{bundle.badge}</Badge>
                        )}
                      </td>
                      <td className="text-right py-2">{bundle.credits}</td>
                      <td className="text-right py-2">{formatCurrency(bundle.price_usd)}</td>
                      <td className="text-right py-2 text-gray-500">{formatCurrency(bundle.projected_cost_usd, 4)}</td>
                      <td className="text-right py-2 text-green-600 font-medium">{formatCurrency(bundle.margin_usd, 2)}</td>
                      <td className="text-right py-2">
                        <MarginIndicator marginPct={bundle.margin_pct} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-purple-400 mt-2 italic">
              Cost per credit: {formatCurrency(derived?.cost_per_credit || 0, 6)} (from simulation)
            </p>
          </div>
        )}
        
        {/* No Data State */}
        {(!subscription_tiers || subscription_tiers.length === 0) && 
         (!credit_bundles || credit_bundles.length === 0) && (
          <div className="text-center py-6 text-gray-500">
            <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No pricing constructs found for projection.</p>
            <p className="text-xs mt-1">Configure subscription tiers and credit bundles in Pricing admin.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PricingImpactProjection;
